import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    waiting: Boolean,
    timeout: { type: Number, default: 8000 }
  }

  connect() {
    // Only start the timer if we're waiting for subscription activation
    if (this.waitingValue) {
      this.setupObserver()
      this.startRefreshTimer()
    }
  }

  disconnect() {
    this.clearRefreshTimer()
    this.disconnectObserver()
  }

  setupObserver() {
    // Watch for changes to the subscription section
    const subscriptionSection = document.getElementById("current-subscription-section")
    const statusMessage = document.getElementById("subscription-status-message")
    
    if (!subscriptionSection) {
      return
    }

    // Create MutationObserver to watch for changes
    this.observer = new MutationObserver(() => {
      this.checkIfUpdated()
    })

    // Observe changes to child elements and text content
    this.observer.observe(subscriptionSection, {
      childList: true,
      subtree: true,
      characterData: true
    })

    // Also observe status message if it exists
    if (statusMessage) {
      this.statusObserver = new MutationObserver(() => {
        this.checkIfUpdated()
      })
      
      this.statusObserver.observe(statusMessage, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"]
      })
    }
  }

  disconnectObserver() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.statusObserver) {
      this.statusObserver.disconnect()
      this.statusObserver = null
    }
  }

  startRefreshTimer() {
    // Clear any existing timer
    this.clearRefreshTimer()
    
    // Set timer to refresh after timeout
    this.refreshTimer = setTimeout(() => {
      this.checkAndRefresh()
    }, this.timeoutValue)
  }

  clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  checkIfUpdated() {
    // Check if subscription has been activated
    const subscriptionSection = document.getElementById("current-subscription-section")
    const statusMessage = document.getElementById("subscription-status-message")
    
    if (!subscriptionSection) {
      return
    }

    // Check if subscription section no longer shows "No Active Subscription"
    const hasSubscription = !subscriptionSection.textContent.includes("No Active Subscription")
    
    // Check if status message no longer has spinner (green background means success)
    const isSuccess = statusMessage && 
                      statusMessage.classList.contains("bg-green-500/10") &&
                      !statusMessage.querySelector(".animate-spin")

    // If subscription is active or success message is shown, cancel timer
    if (hasSubscription || isSuccess) {
      this.clearRefreshTimer()
      this.disconnectObserver()
    }
  }

  checkAndRefresh() {
    // Check if subscription section has been updated
    const subscriptionSection = document.getElementById("current-subscription-section")
    const statusMessage = document.getElementById("subscription-status-message")
    
    if (!subscriptionSection) {
      // Section doesn't exist, refresh
      this.refresh()
      return
    }

    // Check if subscription section still shows "No Active Subscription"
    const noSubscriptionText = subscriptionSection.textContent.includes("No Active Subscription")
    
    // Check if status message still has spinner (blue background means still waiting)
    const stillWaiting = statusMessage && 
                         statusMessage.classList.contains("bg-blue-500/10") &&
                         statusMessage.querySelector(".animate-spin")

    // If still showing "No Active Subscription" or still waiting, refresh
    if (noSubscriptionText || stillWaiting) {
      this.refresh()
    } else {
      // Subscription was updated, clear the timer
      this.clearRefreshTimer()
      this.disconnectObserver()
    }
  }

  refresh() {
    // Remove success parameter to avoid showing the message again
    const url = new URL(window.location.href)
    url.searchParams.delete("success")
    
    // Refresh the page
    window.location.href = url.toString()
  }
}

